import {ActionSource, QuestionDifficulty} from '@domain/pom'
import {TaskSource} from '@domain/tasks'
import {
  Attachment,
  EquipmentData,
  Id,
  Info,
  Iso8601,
  ShiftEventCategory,
  ShiftEventTaskDto,
  User
} from '@hconnect/common/types'

import {TasksFilterKey, TasksPageParamKey} from '../consts'

export {TaskSource}

export enum TaskStatus {
  Pending = 'pending',
  Ongoing = 'ongoing',
  Done = 'done',
  Cancelled = 'cancelled',
  None = 'none',
  WaitingForApproval = 'waitingForApproval',
  Approved = 'approved',
  Rejected = 'rejected',
  WorkOrderCreated = 'workOrderCreated'
}

export type PaginationResponse<T> = {
  items: T[]
  totalItemCount: number
}

export type SapMaintenanceNotification = {
  sapMaintenanceNotificationId?: string
}

export type HProduceTask = {
  status: TaskStatus
  created: Iso8601
  description: string
  dueDate: Iso8601
  externalId: string
  id: string
  plantId: string
  source: TaskSource
  title: string
  assignees: User[]
  sapMaintenanceNotification?: SapMaintenanceNotification
}

export type HProduceTaskIdentifiers = Pick<HProduceTask, 'id' | 'source' | 'plantId' | 'externalId'>

export enum TasksTableKey {
  TITLE = 'title',
  ASSIGNEE = 'assignee',
  STATUS = 'status',
  DUE_DATE = 'dueDate',
  CREATED = 'created',
  SOURCE = 'source',
  PLANT = 'plant',
  MAINTENANCE_NOTIFICATION = 'maintenanceNotification'
}

export enum TasksSortBy {
  TITLE = 'title',
  DUE_DATE = 'dueDate',
  CREATED_AT = 'createdAt',
  STATUS = 'status',
  ASSIGNEE = 'assignee',
  MAINTENANCE_NOTIFICATION_ID = 'maintenanceNotificationId',
  PLANT_NAME = 'plantName'
}

export enum SortOrder {
  ASCENDING = 'ascending',
  DESCENDING = 'descending'
}

export type TasksTableSortOptions = {
  sortBy?: TasksTableKey
  sortOrder?: SortOrder
  onSortChange?: (sortBy: TasksTableKey, sortOrder?: SortOrder) => void
}

export type TasksSortingOptions = {
  sortBy?: TasksSortBy
  sortOrder?: SortOrder
}

export type TaskFilterDto = {
  title?: string
  dueDateFrom?: Iso8601
  dueDateTo?: Iso8601
  createdDateFrom?: Iso8601
  createdDateTo?: Iso8601
  statuses?: TaskStatus[]
  oilAnalysisCriticalities?: string[]
  categories?: ShiftEventCategory[]
  priorities?: number[]
  assigneeIds?: string[]
  createdByIds?: string[]
  plantIds?: string[]
  processStages?: string[]
  mainEquipmentId?: Pick<EquipmentData, 'id' | 'idType'>
  equipmentId?: Pick<EquipmentData, 'id' | 'idType'>
  actionSources?: ActionSource[]
  sectionIds?: string[]
  topicIds?: string[]
  questionIds?: string[]
  questionDifficulties?: QuestionDifficulty[]
  costLeadership?: boolean
}

export type TasksFilterQueryParams = {
  offset: number
  limit: number
  sources?: TaskSource[]
  includeOnlyAssignedToUser?: boolean
  includeOnlyCreatedByUser?: boolean
} & TasksSortingOptions

export type TasksStatusSummaryFilterQueryParams = {
  sources?: TaskSource[]
  includeOnlyAssignedToUser?: boolean
  includeOnlyCreatedByUser?: boolean
}

export const COST_LEADERSHIP_FILTER_VALUES = ['true', 'false'] as const

export type CostLeadershipFilterValue = (typeof COST_LEADERSHIP_FILTER_VALUES)[number]

export const isCostLeadershipFilterValue = (
  value: string | undefined
): value is CostLeadershipFilterValue =>
  COST_LEADERSHIP_FILTER_VALUES.includes(value as CostLeadershipFilterValue)

export type TasksFiltersState = {
  [TasksFilterKey.TITLE]?: string
  [TasksFilterKey.DUE_DATE_RANGE]?: string
  [TasksFilterKey.CREATED_DATE_RANGE]?: string
  [TasksFilterKey.STATUSES]?: TaskStatus[]
  [TasksFilterKey.OIL_ANALYSIS_CRITICALITY]?: string[]
  [TasksFilterKey.ASSIGNEE]?: string
  [TasksFilterKey.CREATED_BY]?: string
  [TasksFilterKey.CATEGORIES]?: ShiftEventCategory[]
  [TasksFilterKey.PRIORITIES]?: number[]
  [TasksFilterKey.AREA]?: string
  [TasksFilterKey.PLANT]?: string
  [TasksFilterKey.PROCESS_STAGES]?: string[]
  [TasksFilterKey.EQUIPMENT]?: EquipmentData
  [TasksFilterKey.MAIN_EQUIPMENT]?: EquipmentData
  [TasksFilterKey.POM_ACTION_SOURCES]?: ActionSource[]
  [TasksFilterKey.POM_SECTIONS]?: string[]
  [TasksFilterKey.POM_TOPICS]?: string[]
  [TasksFilterKey.POM_QUESTIONS]?: string[]
  [TasksFilterKey.POM_QUESTION_CATEGORIES]?: QuestionDifficulty[]
  [TasksFilterKey.POM_COST_LEADERSHIP]?: CostLeadershipFilterValue
}

type TasksQuickFiltersState = {
  [TasksPageParamKey.ASSIGNED_TO_ME]?: boolean
  [TasksPageParamKey.CREATED_BY_ME]?: boolean
  [TasksPageParamKey.SOURCES]?: TaskSource[]
}

export type TasksPageQueryParams = TasksQuickFiltersState & TasksFiltersState & TasksSortingOptions

export type ShiftTaskDto = ShiftEventTaskDto &
  Id &
  Info & {
    attachments?: Attachment[]
  }

export type CreateShiftMaintenanceNotificationActionDto = {
  sourceEventId: string
  workCenterId?: string
  plannerGroupId?: string
}

export type UseTasksParams = {
  limit: number
  pageNumber: number
  assignedToMe: boolean
  createdByMe: boolean
  sources?: TaskSource[]
  filters: TasksFiltersState
  sorting: TasksSortingOptions
  timezone: string
  areaPlantIds?: string[]
}

export type UseTasksStatusesCountsParams = {
  assignedToMe: boolean
  createdByMe: boolean
  sources?: TaskSource[]
  filters: TasksFiltersState
  sorting: TasksSortingOptions
  timezone: string
  areaPlantIds?: string[]
}

export type TaskStatusCountDto = {status: TaskStatus; count: number}

export type TaskAssignee = {
  id: string
  name: string
  email?: string
}
